import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useParams, Outlet, Link, useLocation } from "react-router-dom";

// Components
import HeaderBar from "../Header/HeaderBar";
import OverCreditBanner from "../Paywall/OverCreditBanner";
import LoadingIcon from "../Loaders/LoadingIcon";

// Services
import axiosJobs, {
  JobData,
  JobFeatureCollection,
  SeasonName,
} from "../../services/axios/jobs";

import SiteMapEditor from "./Editor";
import {
  DataProvider,
  SurfacesVisibility,
  useData,
} from "./contexts/DataContext";
import { SimpleLatLng, SiteMapSurfaceFeatureType } from "./Core";
import { mergeJobUpdatePayload } from "../../utils/payloads";
import { AxiosContext } from "../../services/axios/AxiosContext";
import errors from "../../services/errors";
import { useEditState } from "./contexts/EditStateContext";
import { usePaywall } from "./contexts/PaywallContext";
import useNavigateKeepSearchParams from "../../hooks/useNavigateKeepSearchParams";
import { HistoryItem, updateHistory } from "./utils/history";
import { areFeatureCollectionsEqual } from "./utils/data";
import { confirmWrapper } from "../Modals/ConfirmWrapper";
import { useMapEditing } from "./utils/routing";

// TODO: make sure site map features come back as numbers, NOT strings
// as this could break us in unexpected ways
export type SiteMapFeatureType =
  | "Polygon"
  | "Circle"
  | "Marker"
  | "Polyline"
  | "Rectangle";

export type SiteMapSurfaceType = {
  id: number;
  type: SiteMapFeatureType;
  coordinates: SimpleLatLng[][];
  properties: SiteMapFeatureProperties;
};
export type SiteMapFeatureProperties = {
  surfaceType: SiteMapSurfaceFeatureType;
};

export type SurfaceType = {
  color: string; // hex for now
  label: string;
  value: SiteMapSurfaceFeatureType;
};

const parkingLot: SurfaceType = {
  label: "Parking Lot",
  value: "parkingLot",
  color: "#619ee5",
};
const snowPile: SurfaceType = {
  label: "Snow Pile",
  value: "snowPile",
  color: "#fa6cd0",
}; // pink
// const sideWalk: SurfaceType = {
//   label: "Sidewalk",
//   value: "sidewalk",
//   color: "#32CD32",
// }; // green
const other: SurfaceType = { label: "Other", value: "other", color: "#155094" };
const propertySideWalk: SurfaceType = {
  label: "Property Sidewalk",
  value: "propertySidewalk",
  color: "#bf42ec", // purple
};
const muniSideWalk: SurfaceType = {
  label: "MUNI Sidewalk",
  value: "muniSidewalk",
  color: "#a1ff95", // light green
}; // orange
const lawnArea: SurfaceType = {
  label: "Lawn",
  value: "lawn",
  color: "#23be23", // green
};
const rockBed: SurfaceType = {
  label: "Rock Bed",
  value: "rockBed",
  color: "#69fff8", // light blue
};
const landscapeBed: SurfaceType = {
  label: "Landscape Bed",
  value: "landscapeBed",
  color: "#923faf",
};
export const surfaceTypes: Record<SeasonName, SurfaceType[]> = {
  snow: [parkingLot, snowPile, muniSideWalk, propertySideWalk, other],
  landscape: [landscapeBed, rockBed, lawnArea, other],
};

export type AllowedFeatures =
  | BoundaryFeature
  | SurfaceFeature
  | HazardFeature
  | LabelFeature
  | ArrowFeature;

export type ArrowFeature = {
  type: "Feature";
  geometry: {
    type: "LineString";
    coordinates: number[][];
  };
  properties: {
    type: "arrow";
    color: string;
  };
};
export type BoundaryFeature = {
  type: "Feature";
  geometry: {
    type: "LineString";
    coordinates: number[][];
  };
  properties: {
    type: "boundary";
    color: string;
  };
};
export type HazardFeature = {
  type: "Feature";
  geometry: {
    type: "Point";
    coordinates: number[];
  };
  properties: {
    color: string; // COLOR_RED | COLOR_BLACK | COLOR_BLUE;
    type: "hazard";
  };
};
export type LabelFeature = {
  type: "Feature";
  geometry: {
    type: "Point";
    coordinates: number[];
  };
  properties: {
    color: string; // COLOR_RED | COLOR_BLACK | COLOR_BLUE;
    size: "sm" | "md" | "lg";
    text: string;
    type: "label";
  };
};

export type SurfaceFeature = {
  type: "Feature";
  geometry: {
    type: "Polygon";
    coordinates: number[][][];
  };
  properties: {
    surfaceType: string;
    type: "surface";
  };
};

const DataAndMap = ({
  disableUi,
  surfacesVisibility,
  fullScreen = false,
  onMapReady = undefined,
  onStageChange = undefined,
  zoom,
  center,
}: {
  disableUi: boolean;
  surfacesVisibility?: SurfacesVisibility;
  fullScreen?: boolean;
  onMapReady?: (map: google.maps.Map) => void;
  onStageChange?: (
    stage: "loading_data" | "waiting_for_tiles" | "tiles_loaded" | "error_data",
  ) => void;
  zoom?: number;
  center?: SimpleLatLng;
}) => {
  const navigate = useNavigateKeepSearchParams(["return_to"]);
  const axios = useContext(AxiosContext);
  const { setEditing } = useMapEditing();
  const { setIsDirty } = useEditState();
  const { siteMapCreditsUsed } = usePaywall();

  const { jobId, season } = useParams<{ season: SeasonName; jobId: string }>();

  const [headerLabel, setHeaderLabel] = useState("<");
  const [loading, setLoading] = useState(true);

  // because our backend is currently fairly naive, we need to handle merging
  // on the frontend carefully so we don't lose data our frontend doesn't know about
  const [rawData, setRawData] = useState<JobData | null>(null);

  const showControls = disableUi !== true && siteMapCreditsUsed !== true;

  // Refs keep these callbacks out of the fetch effect's dependencies.
  // setEditing is a new function every render, so it would re-fetch in a loop.
  const onStageChangeRef = useRef(onStageChange);
  onStageChangeRef.current = onStageChange;
  const setEditingRef = useRef(setEditing);
  setEditingRef.current = setEditing;
  const setIsDirtyRef = useRef(setIsDirty);
  setIsDirtyRef.current = setIsDirty;

  useEffect(() => {
    setLoading(true);
    onStageChangeRef.current?.("loading_data");
    let draft: HistoryItem<JobFeatureCollection> | null = null;
    updateHistory<JobFeatureCollection>((h) => {
      if (h.isDirty && h.storedRecords > 0) {
        draft = h.current();
      }
    }, `site-map-history-${jobId}-${season}`);
    const abort = new AbortController();
    axios
      .get("job/" + jobId, {
        signal: abort.signal,
      })
      .catch((e) => {
        onStageChangeRef.current?.("error_data");
        errors.report(e);
      })
      .then((r) => {
        if (r) {
          const jobData = r.data as JobData;
          setHeaderLabel(jobData.name);
          if (
            draft &&
            draft.item &&
            draft.item?.features?.length > 0 &&
            !areFeatureCollectionsEqual(
              draft.item,
              jobData[season!].siteMapGeoJson,
            )
          ) {
            console.log(
              "db-value",
              JSON.stringify(jobData[season!].siteMapGeoJson),
            );
            console.log("draft-value", JSON.stringify(draft.item));
            confirmWrapper("Load Draft?", {
              description: `You have unsaved changes from ${new Date(draft.saved_at).toLocaleString()}.  Would you like to load them?`,
              okText: "Yes, load draft",
              cancelText: "No",
            }).then((load: boolean) => {
              if (load) {
                const draftValue = draft?.item;
                console.log("Loading draft");
                console.log(JSON.stringify(draftValue));
                console.log(JSON.stringify(jobData));
                console.log(season);
                setRawData({
                  ...jobData,
                  [season!]: {
                    ...jobData[season!],
                    siteMapGeoJson: draftValue,
                  },
                });
                setIsDirtyRef.current(true);
                setEditingRef.current(true);
              } else {
                console.log("selected no - going to delete draft");
                window.localStorage.removeItem(
                  `site-map-history-${jobId}-${season}`,
                );
                setRawData(jobData);
              }
              onStageChangeRef.current?.("waiting_for_tiles");
            });
          } else {
            window.localStorage.removeItem(
              `site-map-history-${jobId}-${season}`,
            );
            setRawData(jobData);
            onStageChangeRef.current?.("waiting_for_tiles");
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
    return () => {
      abort.abort();
    };
  }, [jobId, axios, season]);

  const handleSaveJob = (featureCollection: JobFeatureCollection) => {
    // TODO: set the appropriate season info here
    // Submit updated job to server
    const updatePayload = mergeJobUpdatePayload(rawData, {
      [season!]: {
        siteMapGeoJson: featureCollection,
      },
    });
    return new Promise((resolve, reject) => {
      axiosJobs.updateJob(
        updatePayload,
        (data) => {
          if (data.successfullyUpdated) {
            setRawData(data.job);
            updateHistory<JobFeatureCollection>((history) => {
              history.isDirty = false; // we just saved
            }, `site-map-history-${jobId}-${season}`);
            resolve(data.job);
          }
          reject();
        },
        reject,
      );
    });
  };

  const handleAutoSave = useCallback(
    async (featureCollection: JobFeatureCollection) => {
      updateHistory<JobFeatureCollection>((history) => {
        const newItemPushed = history.push(
          featureCollection,
          areFeatureCollectionsEqual,
        );
        if (newItemPushed) {
          console.log("Auto-saved.");
        }
      }, `site-map-history-${jobId}-${season}`);
    },
    [jobId, season],
  );

  // TODO: setup delete of a site map option?

  return (
    <div className="flex flex-col items-center">
      {loading || !rawData ? (
        <>
          <HeaderBar title={headerLabel} />
          <LoadingIcon />
        </>
      ) : (
        <DataProvider
          propertyAddress={rawData.address}
          propertyName={rawData.name}
          featureCollection={rawData[season!].siteMapGeoJson}
          surfaceTypes={surfaceTypes[season!]}
          surfacesVisibility={surfacesVisibility}
          onDelete={() => {
            navigate(`edit`);
          }}
          onSave={handleSaveJob}
          onAutoSave={handleAutoSave}
          disableUi={disableUi}
        >
          <DataRedirectsIfNeeded>
            <MapSection
              headerLabel={headerLabel}
              location={rawData.location}
              surfaceTypes={surfaceTypes[season!]}
              disableUi={disableUi}
              showControls={showControls}
              fullScreen={fullScreen}
              onMapReady={onMapReady}
              zoom={zoom}
              center={center}
            />
          </DataRedirectsIfNeeded>
        </DataProvider>
      )}
    </div>
  );
};

export default DataAndMap;

// react to our data setup if needed
// per React Router docs, these items should be done using redirects in loaders
// and actions instead of in components in response to data: https://reactrouter.com/6.30.1/fetch/redirect
// but we're dealing with reality here at the moment ...
const DataRedirectsIfNeeded = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigateKeepSearchParams(["return_to"]);
  const { boundary, surfaceFeatures } = useData();
  const location = useLocation();
  const onExport = location.pathname.includes("export");
  useEffect(() => {
    // if (!boundary) {
    //   navigate("edit/setup-boundary");
    // }
    if (surfaceFeatures.length === 0 && !onExport) {
      navigate("edit");
    }
  }, []); // Empty dependency array ensures this only runs once on mount

  return <>{children}</>;
};

const MapSection = ({
  headerLabel,
  location,
  surfaceTypes,
  disableUi,
  showControls,
  fullScreen = false,
  onMapReady = undefined,
  zoom,
  center,
}: {
  headerLabel: string;
  location: SimpleLatLng;
  surfaceTypes: SurfaceType[];
  disableUi: boolean;
  showControls: boolean;
  fullScreen?: boolean;
  onMapReady?: (map: google.maps.Map) => void;
  zoom?: number;
  center?: SimpleLatLng;
}) => {
  const jobId = useParams().jobId;
  const { siteMapCreditsUsed, siteMapCreditsWarning } = usePaywall();
  return (
    <>
      {!disableUi && (
        <>
          {siteMapCreditsUsed && (
            <OverCreditBanner
              message="You've used your map editing credits for the month."
              linkTo="/admin/settings/billing?show-plans=1"
              linkText="Please upgrade your account to edit more maps each month."
            />
          )}
          {siteMapCreditsWarning && !siteMapCreditsUsed && (
            <OverCreditBanner
              message={siteMapCreditsWarning}
              linkTo="/admin/settings/billing?show-plans=1"
              linkText="Please upgrade your account to edit more maps each month."
            />
          )}
          <HeaderBar
            titleLink={
              <Link
                to={`/admin/property/${jobId}`}
                relative="path"
                className="hover:underline"
              >
                &lt; {headerLabel}{" "}
              </Link>
            }
            buttonSection={<div id="site-map-header-portal" />}
          />
        </>
      )}
      <div className="flex flex-1 flex-col w-full items-center overflow-y-scroll">
        <SiteMapEditor
          location={location}
          surfaceTypes={surfaceTypes}
          showControls={showControls}
          fullScreen={fullScreen}
          onMapReady={onMapReady}
          zoom={zoom}
          center={center}
        >
          <Outlet />
        </SiteMapEditor>
      </div>
    </>
  );
};
